import React from "react";
import { cn } from "../../utils/cn";

interface FleetrifyButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost" | "destructive";
  size?: "small" | "medium" | "large" | "icon";
  className?: string;
  loading?: boolean;
  children: React.ReactNode;
}

export const FleetrifyButton: React.FC<FleetrifyButtonProps> = ({
  variant = "primary",
  size = "medium",
  className,
  children,
  disabled,
  loading = false,
  ...props
}) => {
  const isDisabled = disabled || loading;

  const baseClasses = cn(
    "inline-flex items-center transition-all duration-300 justify-center rounded-md font-medium ",
    {
      "px-6 py-2 text-sm": size === "small",
      "px-8 py-3": size === "medium",
      "px-8 py-4 text-lg": size === "large",
      "p-2 h-8 w-8": size === "icon",
    },
    {
      "bg-[#143887] text-white hover:bg-blue-800":
        variant === "primary" && !isDisabled,
      "bg-gray-200 text-gray-800 hover:bg-gray-300":
        variant === "secondary" && !isDisabled,
      "border border-gray-300 text-gray-800 hover:bg-gray-100":
        variant === "outline" && !isDisabled,
      "text-gray-800 hover:bg-gray-100": variant === "ghost" && !isDisabled,
      "bg-red-500 text-white hover:bg-red-600":
        variant === "destructive" && !isDisabled,
    },
    isDisabled && variant === "primary"
      ? "bg-[#143887]/50 text-white cursor-not-allowed"
      : isDisabled
      ? "bg-gray-300 text-gray-500 !border !border-gray-300 cursor-not-allowed"
      : "",
    className
  );

  return (
    <button className={baseClasses} disabled={isDisabled} {...props}>
      {loading && (
        <svg
          className="animate-spin mr-2 h-4 w-4 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
          />
        </svg>
      )}
      
      {loading ? "loading.." : children}
    </button>
  );
};
